import React, { createContext, useContext, useCallback, useRef } from 'react';

interface HistoryContextType {
  registerRefetch: (refetchFn: () => Promise<void>) => void;
  triggerRefetch: () => void;
}

const HistoryContext = createContext<HistoryContextType | undefined>(undefined);

export const HistoryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const refetchFnRef = useRef<(() => Promise<void>) | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isRefetchingRef = useRef<boolean>(false);
  const registrationCountRef = useRef<number>(0);

  const registerRefetch = useCallback((refetchFn: () => Promise<void>) => {
    registrationCountRef.current++;
    console.log('📚 [HistoryContext] Registering refetch function (count:', registrationCountRef.current, ')');
    refetchFnRef.current = refetchFn;
  }, []);

  const triggerRefetch = useCallback(() => {
    // Clear any pending refetch
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Prevent concurrent refetches
    if (isRefetchingRef.current) {
      console.log('📚 [HistoryContext] Refetch already in progress, skipping');
      return;
    }

    // Debounce refetch calls
    timeoutRef.current = setTimeout(async () => {
      if (refetchFnRef.current) {
        console.log('📚 [HistoryContext] Triggering history refetch');
        isRefetchingRef.current = true;

        try {
          await refetchFnRef.current();
        } catch (error) {
          console.error('📚 [HistoryContext] Refetch failed:', error);
        } finally {
          isRefetchingRef.current = false;
        }
      }
    }, 500);
  }, []);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const value = React.useMemo(
    () => ({ registerRefetch, triggerRefetch }),
    [registerRefetch, triggerRefetch]
  );

  return <HistoryContext.Provider value={value}>{children}</HistoryContext.Provider>;
};

export const useHistory = (): HistoryContextType => {
  const context = useContext(HistoryContext);
  if (!context) {
    throw new Error('useHistory must be used within a HistoryProvider');
  }
  return context;
};
